import { mobileWidth } from '@/constants';
import { useMediaQuery } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import dayjs from '@/config/dayjs';
import { parseAppDate } from '@/utils/date';

/**
 * Use parent value when it is a non-empty parseable date string; otherwise fall back
 * (e.g. Open Year defaults). Preserves the original string format so widget hosts that
 * pass `YYYY-MM-DD` (per StandardWidgetProps) keep API-shaped dates for RTK queries.
 */
function resolveControlledDateProp(
  prop: string | undefined,
  fallback: string | undefined,
): string | undefined {
  const trimmed = prop?.trim();
  if (!trimmed) return fallback;

  return parseAppDate(trimmed) ? trimmed : fallback;
}

interface UseControlledDatesParams {
  /**
   * Start date passed down from a parent component when the widget is being controlled.
   * Used when date filters are adjusted page-wide across many widgets at once.
   */
  propStartDate?: string;
  /**
   * End date passed down from a parent component when the widget is being controlled.
   * Used when date filters are adjusted page-wide across many widgets at once.
   */
  propEndDate?: string;
  /**
   * Default start date value to use when the component is in standalone widget mode.
   * This is the initial value when not being controlled by a parent component.
   */
  initialWidgetStartDate?: string;
  /**
   * Default end date value to use when the component is in standalone widget mode.
   * This is the initial value when not being controlled by a parent component.
   */
  initialWidgetEndDate?: string;
  /**
   * Flag indicating if the component is being used as a standalone widget.
   * When true, initialWidget dates are used as defaults.
   * When false, the component is controlled by propDates from a parent.
   */
  isWidget?: boolean;
}

interface UseControlledDatesResult {
  startDate: string | undefined;
  endDate: string | undefined;
  /**
   * Updates the start date value.
   * Widgets should ONLY use this function to update the date.
   * This should be used when a widget is standalone (isWidget is true).
   * If the widget is controlled by the page, the page passes the props instead.
   */
  setStartDate: (date: string | undefined) => void;

  /**
   * Updates the end date value.
   * Widgets should ONLY use this function to update the date.
   * This should be used when a widget is standalone (isWidget is true).
   * If the widget is controlled by the page, the page passes the props instead.
   */
  setEndDate: (date: string | undefined) => void;
  resetDates: () => void;
}

/**
 * Hook for managing controlled date states that can be overridden by props
 * The primary use case of this is for page-level date filters to update the
 * start and end date values and cause all widgets within the page to update
 * the Redux queries they are using. This hook helps the widget remain flexible
 * as widgets will often have to use date range pickers within themselves on the
 * dashboard or when rendered externally.
 *
 * When being rendered externally, the widgets should pass their default initial
 * values to the `propStartDate` and `propEndDate` parameters.
 *
 * @param {UseControlledDatesParams} params - The start and end date props
 * @returns {UseControlledDatesResult} - The controlled date states and setters
 */
export function useControlledDates({
  propStartDate,
  propEndDate,
  initialWidgetStartDate,
  initialWidgetEndDate,
  isWidget = false,
}: UseControlledDatesParams): UseControlledDatesResult {
  const [internalStartDate, setInternalStartDate] = useState<
    string | undefined
  >(() => resolveControlledDateProp(propStartDate, initialWidgetStartDate));
  const [internalEndDate, setInternalEndDate] = useState<string | undefined>(
    () => resolveControlledDateProp(propEndDate, initialWidgetEndDate),
  );

  // Use refs to track previous prop values to detect changes
  const prevPropStartDateRef = useRef(propStartDate);
  const prevPropEndDateRef = useRef(propEndDate);

  useEffect(() => {
    setInternalStartDate(
      resolveControlledDateProp(propStartDate, initialWidgetStartDate),
    );
    prevPropStartDateRef.current = propStartDate;
  }, [propStartDate, initialWidgetStartDate]);

  useEffect(() => {
    setInternalEndDate(
      resolveControlledDateProp(propEndDate, initialWidgetEndDate),
    );
    prevPropEndDateRef.current = propEndDate;
  }, [propEndDate, initialWidgetEndDate]);

  useEffect(() => {
    if (isWidget) {
      setInternalStartDate(initialWidgetStartDate);
      setInternalEndDate(initialWidgetEndDate);
    }
  }, []);

  // Function to reset both dates
  const resetDates = () => {
    if (!propStartDate) {
      setInternalStartDate(isWidget ? initialWidgetStartDate : propStartDate);
    }
    if (!propEndDate) {
      setInternalEndDate(isWidget ? initialWidgetEndDate : propEndDate);
    }
  };

  return {
    startDate: internalStartDate,
    endDate: internalEndDate,
    setStartDate: setInternalStartDate,
    setEndDate: setInternalEndDate,
    resetDates,
  };
}

interface UsePageDatesParams {
  initialPageStartDate: string;
  initialPageEndDate: string;
}

interface UsePageDatesResult {
  startDate: string | undefined;
  endDate: string | undefined;
  setStartDate: (date: string | undefined) => void;
  setEndDate: (date: string | undefined) => void;
  showDateRange: boolean;
  setShowDateRange: (show: boolean) => void;
  toggleDateRange: () => void;
  handleDateRangeChange: (startDate: string, endDate: string) => void;
}

export function usePageDates({
  initialPageStartDate,
  initialPageEndDate,
}: UsePageDatesParams): UsePageDatesResult {
  const [internalStartDate, setInternalStartDate] = useState<
    string | undefined
  >(initialPageStartDate);
  const [internalEndDate, setInternalEndDate] = useState<string | undefined>(
    initialPageEndDate,
  );
  const isMobileMatch = useMediaQuery(mobileWidth);

  const [showDateRange, setShowDateRange] = useState<boolean>(false);
  const toggleDateRange = () => {
    setShowDateRange(!showDateRange);
  };

  const janFirstCurrentYear = (): string => `01/01/${new Date().getFullYear()}`;

  // Repair legacy state: single-date header updates cleared start to "" while end stayed valid.
  useEffect(() => {
    const end = internalEndDate?.trim();
    const start = internalStartDate?.trim();
    if (!end || !dayjs(end, 'MM/DD/YYYY').isValid()) return;
    if (start && dayjs(start, 'MM/DD/YYYY').isValid()) return;
    setInternalStartDate(janFirstCurrentYear());
  }, [internalStartDate, internalEndDate]);

  const ensureDateRangeOrder = (
    start: string,
    end: string,
  ): [string, string] => {
    const startParsed = dayjs(start, 'MM/DD/YYYY');
    const endParsed = dayjs(end, 'MM/DD/YYYY');

    if (
      startParsed.isValid() &&
      endParsed.isValid() &&
      startParsed.isAfter(endParsed)
    ) {
      return [end, start];
    }
    return [start, end];
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    const endTrim = endDate?.trim() ?? '';
    const startTrim = startDate?.trim() ?? '';
    const endValid = Boolean(endTrim && dayjs(endTrim, 'MM/DD/YYYY').isValid());
    const startValid = Boolean(
      startTrim && dayjs(startTrim, 'MM/DD/YYYY').isValid(),
    );
    const resolvedStart =
      endValid && !startValid ? janFirstCurrentYear() : startTrim || startDate;

    const [orderedStart, orderedEnd] = ensureDateRangeOrder(
      resolvedStart,
      endTrim || endDate,
    );
    setInternalStartDate(orderedStart);
    setInternalEndDate(orderedEnd);

    // Close the date range panel after selection on mobile
    if (isMobileMatch) {
      setShowDateRange(false);
    }
  };
  return {
    startDate: internalStartDate,
    endDate: internalEndDate,
    setStartDate: setInternalStartDate,
    setEndDate: setInternalEndDate,
    showDateRange,
    setShowDateRange,
    toggleDateRange,
    handleDateRangeChange,
  };
}
